import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import "@/mobile.css";
import "@/auth.css";

import Login from "@/Login";
import Shell from "@/Shell";
import NewRecordModal from "@/NewRecordModal";
import Dashboard from "@/pages/Dashboard";
import Tours from "@/pages/Tours";
import TourDetail from "@/pages/TourDetail";
import Customers from "@/pages/Customers";
import Finance from "@/pages/Finance";
import BankAccounts from "@/pages/BankAccounts";
import Reports from "@/pages/Reports";
import { api } from "@/util";

function Workspace({ user, onLogout }) {
  const [tours, setTours] = useState([]);
  const [finance, setFinance] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dashboardData, setDashboardData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitial, setModalInitial] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [t, f, c, a, d] = await Promise.all([
        api.get("/tours"),
        api.get("/finance"),
        api.get("/customers"),
        api.get("/bank-accounts"),
        api.get("/dashboard"),
      ]);
      setTours(t.data);
      setFinance(f.data);
      setCustomers(c.data);
      setAccounts(a.data);
      setDashboardData(d.data);
    } catch (err) {
      // noop; auth gate handles 401
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openNew = (type) => {
    setModalInitial(type || null);
    setModalOpen(true);
  };

  const pendingCount = finance.filter((x) => x.status === "Bekliyor").length;
  const upcomingCount = (dashboardData.upcoming_tours || []).length;

  return (
    <>
      <Shell
        user={user}
        pendingCount={pendingCount}
        upcomingCount={upcomingCount}
        onNew={() => openNew(null)}
        onLogout={onLogout}
      >
        <Routes>
          <Route path="/" element={<Dashboard data={dashboardData} user={user} onNew={openNew} />} />
          <Route path="/turlar" element={<Tours tours={tours} user={user} onNew={openNew} onChanged={loadAll} />} />
          <Route path="/turlar/:id" element={<TourDetail user={user} />} />
          <Route path="/musteriler" element={<Customers customers={customers} user={user} onNew={openNew} onChanged={loadAll} />} />
          <Route path="/finans" element={<Finance finance={finance} user={user} onNew={openNew} onChanged={loadAll} />} />
          <Route path="/hesaplar" element={<BankAccounts accounts={accounts} user={user} onNew={openNew} onChanged={loadAll} />} />
          <Route path="/raporlar" element={<Reports />} />
          <Route path="*" element={<Dashboard data={dashboardData} user={user} onNew={openNew} />} />
        </Routes>
      </Shell>
      <NewRecordModal
        open={modalOpen}
        initial={modalInitial}
        user={user}
        accounts={accounts}
        tours={tours}
        onClose={() => setModalOpen(false)}
        onCreated={() => loadAll()}
      />
      <Toaster position="bottom-right" richColors />
    </>
  );
}

function AuthApp() {
  const [state, setState] = useState("checking");
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((response) => {
        setUser(response.data);
        setState("authenticated");
      })
      .catch(() => setState("anonymous"));
  }, []);

  const signOut = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      /* noop */
    }
    setUser(null);
    setState("anonymous");
  };

  if (state === "checking") {
    return <div className="auth-loading" data-testid="auth-loading">Voyage360 yükleniyor…</div>;
  }
  if (state === "anonymous") {
    return (
      <Login
        onAuthenticated={(nextUser) => {
          setUser(nextUser);
          setState("authenticated");
        }}
      />
    );
  }
  return (
    <BrowserRouter>
      <Workspace user={user} onLogout={signOut} />
    </BrowserRouter>
  );
}

export default AuthApp;
